import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { IconButton, ThemeProvider, StyledEngineProvider, Drawer, lighten } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useRecoilValue } from 'recoil';
import { useLocation } from 'react-router-dom';
import { intersection } from 'lodash';

import NavLink from '../NavLink';
import { lightTheme } from './themes';
import { userState } from '../../atoms/States';

const useStyles = makeStyles()((theme) => ({
  link: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 200,
    fontSize: 18,
    color: '#545454',
    fontFamily: 'AvenirNext',
    fontWeight: 700,
    textDecoration: 'none',
    padding: '15px 20px',
    borderBottom: '1px solid #dddddd',
    '&:hover': {
      backgroundColor: lighten(theme.palette.primary.main, 0.7),
    },
    '&.active': {
      position: 'relative',
      backgroundColor: lighten(theme.palette.primary.main, 0.7),
      '&::before': {
        display: 'inline-block',
        width: 3,
        height: 3,
        position: 'absolute',
        left: 0,
        bottom: 0,
        top: 0,
        margin: 'auto',
        backgroundColor: '#000',
      },
    },
  },
  openNewTabIcon: {
    fontSize: 18,
    marginLeft: 8,
    color: '#888',
  },
  menu: {
    height: 'calc(100% - 70px)',
  },
}));

const BILLING_DASHBOARD_SCOPES = ['super-admin', 'admin', 'finance', 'client-success', 'ops-manager', 'cs-manager', 'ops-approver'];
const HEALTHCARE_MANAGER_SCOPES = ['healthcare-manager'];
const DETECTION_REQUEST_SCOPES = ['super-admin', 'driver-admin'];
const SCOPE_REDACT_CUSTOMER_DATA = ['redact-customer-data', 'super-admin'];
const SERVICE_LEVEL_AGREEMENT_SCOPES = ['super-admin', 'admin'];
const WAREHOUSE_PARKING_SCOPES = ['super-admin', 'admin', 'warehouse-manager', 'warehouse-staff'];

export default function AxlDrawer() {
  const { classes } = useStyles();
  const location = useLocation();
  const [menuSelected, setMenuSelected] = React.useState(null);
  const [state, setState] = React.useState(false);
  const user = useRecoilValue(userState);

  // Variables
  const menus = [
    { label: 'Client', link: '/', otherActive: '/clients' },
    { label: 'DSP', link: '/dsp' },
    { label: 'Warehouse', link: '/warehouses' },
    { label: 'Parking QR Codes', link: '/parking-qr', scopes: WAREHOUSE_PARKING_SCOPES },
    { label: 'Pickup ETA Configuration', link: '/pickup-eta' },
    { label: 'Label Template', link: '/templates' },
    { label: 'SMS', link: '/sms' },
    { label: 'Report', link: '/report' },
    { label: 'User', link: '/users' },
    { label: 'Forecast Management', link: '/forecast-management' },
    {
      label: 'Booking Template',
      link: '/booking-templates',
      scopes: ['super-admin'],
    },
    { label: 'Prefixes management', link: '/prefixes-management' },
    {
      label: 'POD management',
      link: `${process.env.REACT_APP_EXTERNAL_PORTAL_URL}/pod-management`,
    },
    { label: 'SFTP Jobs', link: '/sftp-jobs', scopes: ['super-admin'] },
    { label: 'Map', link: '/maps' },
    { label: 'Appeals', link: '/driver-appeals' },
    { label: 'Complaints', link: '/driver-complaints' },
    {
      label: 'Health benefit payment request',
      link: '/health-benefit-payment-requests',
      scopes: HEALTHCARE_MANAGER_SCOPES,
    },
    {
      label: 'Health benefit',
      link: '/health-benefits',
      scopes: HEALTHCARE_MANAGER_SCOPES,
    },
    { label: 'Email logs', link: '/email-logs' },
    // { label: "Drivers", link: "/drivers" },
    { label: 'Assignment feedbacks', link: '/assignment-feedbacks' },
    {
      label: 'Billing Disposition Dashboard',
      link: '/billing-disposition-dashboard',
      scopes: BILLING_DASHBOARD_SCOPES,
    },
    {
      label: 'Expired Docs Review',
      link: '/detection-request',
      scopes: DETECTION_REQUEST_SCOPES,
    },
    {
      label: 'Customer Data Redaction',
      link: '/redact-customer-data',
      scopes: SCOPE_REDACT_CUSTOMER_DATA,
    },
    { label: 'Region Configuration', link: '/regions' },
    {
      label: 'Legal Management',
      link: '/legal-management',
      scopes: ['super-admin', 'legal'],
    },
    {
      label: 'Service Level Agreement',
      link: '/service-level-agreements',
      scopes: SERVICE_LEVEL_AGREEMENT_SCOPES,
    },
    {
      label: 'Directory Map Image Review',
      link: '/directory-map-image-review',
      scopes: ['super-admin', 'admin'],
    },
  ];

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setState(open);
  };

  function handleMenuClick(e) {
    setMenuSelected(e.target);
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={lightTheme}>
        <React.Fragment key={'left'}>
          <IconButton className={classes.menuButton} color="inherit" aria-label="Open drawer" onClick={toggleDrawer(true)} size="large">
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor={'left'}
            open={state}
            onClose={toggleDrawer(false)}
            slotProps={{
              paper: { className: classes.menu },
            }}
          >
            {menus.map((menu) => {
              if (!!menu.scopes && !!menu.scopes.length) {
                if (intersection(menu.scopes, user.scopes).length === 0) {
                  return null;
                }
              }

              if (menu.link.startsWith('http')) {
                return (
                  <a key={menu.link} className={classes.link} href={menu.link} target="_blank" rel="noopener noreferrer" onClick={handleMenuClick}>
                    {menu.label}
                    <OpenInNewIcon className={classes.openNewTabIcon} />
                  </a>
                );
              }

              return (
                <NavLink location={location} key={menu.link} className={classes.link} to={menu.link} otherActive={menu.otherActive} onClick={handleMenuClick}>
                  {menu.label}
                </NavLink>
              );
            })}
          </Drawer>
        </React.Fragment>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
